import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
    /** ISO timestamp from data.generated_at, or null while loading. */
    generatedAt: string | null;
}

/**
 * "Updated just now / 14s ago / 2m ago" with a tiny pulsing green dot.
 * Ticks once a second, no external deps. Hides itself entirely if there's
 * no timestamp yet so the page never shows a blank "Updated —".
 */
const LiveIndicator: React.FC<Props> = ({ generatedAt }) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);

    if (!generatedAt) return null;

    const generatedMs = new Date(generatedAt).getTime();
    if (isNaN(generatedMs)) return null;

    const diffSec = Math.max(0, Math.floor((now - generatedMs) / 1000));

    return (
        <div className="av2-live" aria-live="polite">
            <span className="av2-live__dot" aria-hidden="true" />
            <span className="av2-live__text">{renderAgo(diffSec)}</span>
        </div>
    );
};

const renderAgo = (sec: number): React.ReactNode => {
    if (sec < 5) {
        return (
            <FormattedMessage
                id="analyticsV2.live.justNow"
                defaultMessage="Updated just now"
            />
        );
    }
    if (sec < 60) {
        return (
            <FormattedMessage
                id="analyticsV2.live.secondsAgo"
                defaultMessage="Updated {n}s ago"
                values={{ n: sec }}
            />
        );
    }
    const min = Math.floor(sec / 60);
    if (min < 60) {
        return (
            <FormattedMessage
                id="analyticsV2.live.minutesAgo"
                defaultMessage="Updated {n}m ago"
                values={{ n: min }}
            />
        );
    }
    const h = Math.floor(min / 60);
    if (h < 24) {
        return (
            <FormattedMessage
                id="analyticsV2.live.hoursAgo"
                defaultMessage="Updated {n}h ago"
                values={{ n: h }}
            />
        );
    }
    const d = Math.floor(h / 24);
    return (
        <FormattedMessage
            id="analyticsV2.live.daysAgo"
            defaultMessage="Updated {n}d ago"
            values={{ n: d }}
        />
    );
};

export default LiveIndicator;
